import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { Button, Collapse, CollapseProps, Space } from "antd";
import { CSSProperties, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import ApproverIcon from "../../../assets/icons/ApproverIcon";
import BackButtonIcon from "../../../assets/icons/BackButtonIcon";
import EditButtonIcon from "../../../assets/icons/EditButtonIcon";
import LogIcon from "../../../assets/icons/LogIcon";
import SettingIcon from "../../../assets/icons/SettingIcon";
import UserIcon from "../../../assets/icons/UserIcon";
import EmployeeInfoItem from "../../../components/common/EmployeeInfoItem";
import InfoItem from "../../../components/common/InfoItem";
import InfoTitle from "../../../components/common/InfoTitle";
import TopCard from "../../../components/common/TopCard";
import UserWithImageNameTitle from "../../../components/common/UserWithImageNameTitle";
import EditLeaveModal from "../../../components/Leave/LeaveRequest/EditLeaveModal";
import LeaveConfirmModal from "../../../components/Leave/LeaveRequest/LeaveConfirmModal";
import LeaveHistoryModal from "../../../components/Leave/LeaveRequest/LeaveHistoryModal";
import Loader from "../../../components/ui/Loader";
import { IMAGE_URL } from "../../../constants/constants";
import { useGetLeaveApplicationDetailsQuery } from "../../../features/leaveApplication/leaveApplicationApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate, formatTime, formatUTCDate } from "../../../lib/time";
import { capitalize } from "../../../utils/string";
import { backLink, underscoreToCapitalizeString } from "../../../utils/utils";
import NotFound from "../../NotFound";

enum Status {
  accepted = "bg-status-success-text",
  pending = "bg-[#ffc700]",
  rejected = "bg-status-danger-text",
  canceled = "bg-status-danger-text",
}

export type LeaveConfirmModalType = "REJECT" | "APPROVE" | "CANCEL" | "BALANCE";

export default function LeaveRequestDetails() {
  const location = useLocation();
  const [collapse, setCollapse] = useState<string | string[]>();
  const { open, handleClose, handleOpen } = useModal<
    boolean,
    LeaveConfirmModalType
  >();
  const { viewId } = useParams();
  const { authInfo } = useAppSelector((state) => state.auth);
  const { isLoading, isError, error, data } =
    useGetLeaveApplicationDetailsQuery(Number(viewId), {
      skip: !viewId,
    });
  useError(isError, error, "Failed fetching leave application details");

  const approversDetails = data?.data?.approvers_details;
  const employeeInfo = data?.data?.leave_application;
  const log = data?.data?.leave_history;

  function canProcessRequest(): boolean {
    const targetApprover = approversDetails?.find(
      (approver) => approver?.business_member_id === authInfo?.id
    );

    if (!targetApprover) {
      return false;
    }

    if (targetApprover?.status !== "pending") {
      return false;
    }

    const precedingOrders = approversDetails?.filter(
      (approver) => approver?.order < targetApprover?.order
    );

    return (
      precedingOrders?.every((approver) => approver?.status !== "pending") ||
      false
    );
  }

  const getItems: (panelStyle: CSSProperties) => CollapseProps["items"] = (
    panelStyle
  ) => [
    {
      key: "1",
      label: (
        <div className="flex justify-between">
          Supper Admin
          {collapse?.includes("1") ? (
            <CaretUpOutlined />
          ) : (
            <CaretDownOutlined />
          )}
        </div>
      ),
      children: (
        <div className="mt-2">
          <p className="text-display-body text-sm font-normal leading-normal mb-2.5">
            Do you want to accept or reject or cancel the request ? Your
            overriding action will bring change in Coworker's leave status .
          </p>
          {authInfo?.role === "Admin" ? (
            <Space
              className={`w-full ${
                employeeInfo?.status !== "pending" ? "justify-center" : ""
              }`}
              size={12}
            >
              {employeeInfo?.status === "pending" ? (
                <>
                  <Button
                    type="primary"
                    className="middle-button !bg-status-success-text min-w-32"
                    onClick={() => handleOpen({ type: "APPROVE", data: true })}
                  >
                    Approve
                  </Button>
                  <Button
                    type="primary"
                    className="middle-button !bg-highlight min-w-32"
                    onClick={() => handleOpen({ type: "REJECT", data: true })}
                  >
                    Reject
                  </Button>
                </>
              ) : null}
              {/* employeeInfo?.status !== "accepted" && */}
              {employeeInfo?.status !== "canceled" ? (
                <Button
                  className="normal-dark-button !font-medium min-w-32"
                  onClick={() => handleOpen({ type: "CANCEL", data: true })}
                >
                  Cancel
                </Button>
              ) : null}
            </Space>
          ) : null}
        </div>
      ),
      style: panelStyle,
    },
  ];

  function getFormatDate(start_date?: string, end_date?: string): string {
    const startDate = start_date ? formatUTCDate(start_date) : "";
    const endDate = end_date ? formatUTCDate(end_date) : "";

    if (startDate === endDate) {
      return startDate;
    }

    if (!startDate && !endDate) {
      return "-";
    }

    return `${startDate} - ${endDate}`;
  }

  if (error && "status" in error && error.status === 404) {
    return <NotFound title="Sorry, data not found" />;
  }

  return (
    <SidebarLayout title="Leave Request Details">
      {open.type === "UPDATE" && (
        <EditLeaveModal open={open.open} handleClose={handleClose} />
      )}

      {(open.type === "REJECT" ||
        open.type === "APPROVE" ||
        open.type === "CANCEL") && (
        <LeaveConfirmModal
          open={open.open}
          data={open?.data}
          type={open?.type}
          handleClose={handleClose}
        />
      )}

      {open.type === "BALANCE" && (
        <LeaveHistoryModal
          open={open.open}
          data={employeeInfo?.business_member?.id}
          handleClose={handleClose}
        />
      )}

      <TopCard
        leftChildren={
          <Link to={backLink(location.pathname, 2)}>
            <Button className="normal-dark-button" icon={<BackButtonIcon />}>
              Back
            </Button>
          </Link>
        }
      >
        {employeeInfo?.status === "pending" && canProcessRequest() ? (
          <>
            <Button
              type="primary"
              className="middle-button !bg-status-success-text min-w-32"
              onClick={() => handleOpen({ type: "APPROVE", data: false })}
            >
              Approve
            </Button>
            <Button
              type="primary"
              className="middle-button !bg-highlight min-w-32"
              onClick={() => handleOpen({ type: "REJECT", data: false })}
            >
              Reject
            </Button>
          </>
        ) : null}

        <Link to="/admin/settings/leave/leave-prorate">
          <Button
            type="primary"
            className="middle-button"
            icon={<SettingIcon />}
          />
        </Link>
      </TopCard>

      {isLoading ? (
        <Loader />
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-6 bg-white rounded-md border border-extra-light">
            <Space
              className="w-full mb-6 justify-between"
              align="center"
              size={8}
            >
              <InfoTitle
                buttonClassName="disabled:!text-neutral"
                icon={<UserIcon />}
              >
                Employee Info
              </InfoTitle>
              <Button
                className="middle-button !text-[#6E8EFF] !border-none"
                icon={<EditButtonIcon width={25} height={24} />}
                size="small"
                onClick={() =>
                  handleOpen({
                    type: "UPDATE",
                  })
                }
              />
            </Space>

            <EmployeeInfoItem
              img={employeeInfo?.business_member?.profile?.pro_pic}
              name={employeeInfo?.business_member?.name || ""}
              id={employeeInfo?.business_member?.employee_id || ""}
              designation={employeeInfo?.business_member?.role.name || ""}
              department={employeeInfo?.business_member?.department || ""}
              email={employeeInfo?.business_member?.profile?.email || ""}
            />

            <div className="mt-4">
              <InfoItem
                title="Status"
                rootClassName="border-t"
                titleClassName="!w-[130px]"
              >
                <span
                  className={`text-white px-2 py-1 rounded text-xs ${
                    Status[employeeInfo?.status as keyof typeof Status]
                  }`}
                >
                  {employeeInfo?.status
                    ? capitalize(
                        employeeInfo?.status === "accepted"
                          ? "approved"
                          : employeeInfo?.status
                      )
                    : "N/A"}
                </span>
              </InfoItem>
              <InfoItem title="Contacts" titleClassName="!w-[130px] py-1">
                <a
                  href={`mailto:${employeeInfo?.business_member.profile?.email}`}
                  className="!text-accent hover:underline"
                >
                  {employeeInfo?.business_member?.profile?.email}
                </a>
              </InfoItem>
              <InfoItem title="Leave Period" titleClassName="!w-[130px] py-1">
                {getFormatDate(
                  employeeInfo?.start_date,
                  employeeInfo?.end_date
                )}
                {/* {employeeInfo?.start_date
                  ? formatUTCDate(employeeInfo?.start_date)
                  : ""}{" "}
                -{" "}
                {employeeInfo?.end_date
                  ? formatUTCDate(employeeInfo?.end_date)
                  : ""} */}
              </InfoItem>
              <InfoItem title="Days" titleClassName="!w-[130px] py-1">
                {employeeInfo?.total_days}
              </InfoItem>
              {employeeInfo?.half_day_configuration ? (
                <InfoItem title="Half day" titleClassName="!w-[130px] py-1">
                  {underscoreToCapitalizeString(
                    employeeInfo?.half_day_configuration
                  )}
                </InfoItem>
              ) : null}
              <InfoItem title="Type" titleClassName="!w-[130px] py-1">
                {employeeInfo?.leave_type?.title}
              </InfoItem>
              <InfoItem title="Balance" titleClassName="!w-[130px] py-1">
                {data?.data?.leave_balance?.remaining_days}/
                {data?.data?.leave_balance?.total_days}{" "}
                <Button
                  type="text"
                  size="small"
                  className="ml-2 !text-accent !font-medium"
                  onClick={() => handleOpen({ type: "BALANCE" })}
                >
                  Leave History
                </Button>
              </InfoItem>
              <InfoItem title="Requested on" titleClassName="!w-[130px] py-1">
                {formatDate(employeeInfo?.created_at)}
              </InfoItem>
              <InfoItem title="Note" titleClassName="!w-[130px] py-1">
                {employeeInfo?.note || "N/A"}
              </InfoItem>
              <InfoItem
                title="Attachment"
                titleClassName="!w-[130px] py-1"
                className={
                  employeeInfo?.file ? "!text-accent hover:underline" : ""
                }
              >
                {employeeInfo?.file ? (
                  <a
                    download
                    href={IMAGE_URL + employeeInfo?.file}
                    target="_blank"
                    rel="noreferrer"
                    className="!text-accent"
                  >
                    File
                  </a>
                ) : undefined}
              </InfoItem>
              <InfoItem
                title="Substitute"
                rootClassName="border-b-0 !pb-0"
                titleClassName="!w-[130px] pt-1"
              >
                {employeeInfo?.substitute?.name || "No Substitute Available"}
              </InfoItem>
            </div>
          </div>

          <div>
            <div className="p-6 bg-white rounded-md border border-extra-light mb-6">
              <InfoTitle
                className="mb-6"
                buttonClassName="disabled:!text-neutral"
                icon={<ApproverIcon />}
              >
                Approver (s)
              </InfoTitle>

              {approversDetails?.map((approver, index) => (
                <div
                  className="flex items-center py-1.5 first:pt-0 last:pb-0 border-b border-extra-light last:border-b-0"
                  key={index}
                >
                  <div className="w-9 text-extra-gray text-sm font-medium">
                    {index + 1}
                  </div>
                  <UserWithImageNameTitle
                    img={approver?.approver_profile}
                    name={approver?.approver_name}
                    title={approver?.approver_designation}
                  />

                  <span
                    className={`text-white px-2 py-1 rounded text-xs ${
                      Status[approver?.status as keyof typeof Status]
                    }`}
                  >
                    {approver?.status
                      ? capitalize(
                          approver?.status === "accepted"
                            ? "approved"
                            : approver?.status
                        )
                      : "N/A"}
                  </span>
                </div>
              ))}

              <Collapse
                rootClassName="leave-super-admin mt-5"
                className="!p-0"
                bordered={false}
                items={getItems({})}
                onChange={(val) => {
                  setCollapse(val);
                }}
              />
            </div>

            <div className="p-6 bg-white rounded-md border border-extra-light mb-6">
              <InfoTitle
                className="mb-6"
                buttonClassName="disabled:!text-neutral"
                icon={<LogIcon />}
              >
                Super Admin Log
              </InfoTitle>

              {log?.map((item) => (
                <div
                  className="flex items-center py-4 first:pt-0 last:pb-0 border-b border-extra-light last:border-b-0"
                  key={item?.id}
                >
                  <div className="w-9">
                    <span className="w-2.5 h-2.5 bg-complete rounded-full inline-block" />
                  </div>
                  <div className="flex-1">
                    <h6 className="text-tint-neutral text-sm font-semibold mb-[3px]">
                      {formatTime(item?.created_at)} -{" "}
                      {formatDate(item?.created_at)}
                    </h6>

                    <p className="text-display-body leading-7">{item?.log}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </SidebarLayout>
  );
}
